import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { AnimatePresence } from "framer-motion";
import { createChart } from "lightweight-charts";
import { MAIN_API_BASE } from "../config";
import { Menu, TrendingUp, TrendingDown } from 'lucide-react';
import { useParams } from 'react-router-dom';

// Import all necessary modal and sub-components
import BinaryTradeModal from '../components/BinaryTradeModal';
import BinaryResultModal from '../components/BinaryResultModal';
import CoinSelectMenu from '../components/CoinSelectMenu';
import CountdownModal from '../components/CountdownModal';
import BeautifulLoader from "../components/BeautifulLoader";
import OrderBTC from "../components/orderbtc";

const COINS = [
  { symbol: "BTC", name: "Bitcoin", tv: "BINANCE:BTCUSDT" },
  { symbol: "ETH", name: "Ethereum", tv: "BINANCE:ETHUSDT" },
  { symbol: "SOL", name: "Solana", tv: "BINANCE:SOLUSDT" },
  { symbol: "XRP", name: "Ripple", tv: "BINANCE:XRPUSDT" },
  { symbol: "TON", name: "Toncoin", tv: "BINANCE:TONUSDT" },
];

// --- Helper Functions (Unchanged) ---
function persistTradeState(tradeState) {
  if (tradeState) localStorage.setItem("activeTrade", JSON.stringify(tradeState));
  else localStorage.removeItem("activeTrade");
}
function loadTradeState() {
  try {
    const saved = localStorage.getItem("activeTrade");
    if (!saved) return null;
    const trade = JSON.parse(saved);
    return trade.endAt > Date.now() ? trade : null;
  } catch { return null; }
}
function createTradeState(trade_id, user_id, duration, amount, symbol, direction) {
  const endAt = Date.now() + duration * 1000;
  return { trade_id, user_id, duration, endAt, amount, symbol, direction };
}
const parseJwt = (token) => {
  try { return JSON.parse(atob(token.split(".")[1])); } 
  catch { return {}; }
};

// --- Child Component for Coin Statistics ---
const CoinStatsDisplay = ({ stats }) => {
    if (!stats) {
        return <div className="h-[76px]"></div>; // This empty div holds the space without showing a loader
    }
    const price = stats.quote.USD.price;
    const change = stats.quote.USD.percent_change_24h;
    const isPositive = change >= 0;

    return (
        <div className="flex justify-between items-start p-4">
            <div>
                <p className="text-3xl font-bold tracking-tighter text-white">
                    {price.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </p>
                <p className={`text-lg font-bold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                    {isPositive ? '+' : ''}{change.toFixed(2)}%
                </p>
            </div>
            <div className="text-right text-sm text-gray-400">
                <p>24h High: <span className="font-semibold text-gray-200">{stats.high_24h.toLocaleString()}</span></p>
                <p>24h Low: <span className="font-semibold text-gray-200">{stats.low_24h.toLocaleString()}</span></p>
                <p>24h Vol: <span className="font-semibold text-gray-200">{(stats.quote.USD.volume_24h / 1_000_000).toFixed(2)}M</span></p>
            </div>
        </div>
    );
};

const formatChartPrice = (value) => {
    if (!Number.isFinite(value)) return "--";
    return value >= 1000
        ? value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 });
};

const formatCompactPrice = (value) => {
    if (!Number.isFinite(value)) return "--";
    if (value >= 1000) {
        return `${(value / 1000).toFixed(2)}K`;
    }
    return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

function normalizeHistoryPoints(points) {
    return (Array.isArray(points) ? points : [])
        .map((point) => ({
            time: Number(point?.time),
            value: Number(point?.value),
        }))
        .filter((point) => Number.isFinite(point.time) && Number.isFinite(point.value) && point.value > 0)
        .sort((a, b) => a.time - b.time)
        .filter((point, index, items) => index === 0 || point.time !== items[index - 1].time)
        .slice(-180);
}

function buildFlowingPoints(previousPoint, nextPoint) {
    if (!previousPoint) return [nextPoint];

    const startTime = Number(previousPoint.time);
    const endTime = Number(nextPoint.time);
    const startValue = Number(previousPoint.value);
    const endValue = Number(nextPoint.value);

    if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) {
        return [nextPoint];
    }

    const gapSeconds = endTime - startTime;
    const steps = Math.max(2, Math.min(8, gapSeconds));
    const delta = endValue - startValue;
    const baseVolatility = Math.max(Math.abs(delta) * 0.16, endValue * 0.00045);
    const points = [];

    for (let step = 1; step <= steps; step += 1) {
        const ratio = step / steps;
        const eased = 1 - Math.pow(1 - ratio, 2.35);
        const driftA = Math.sin(ratio * Math.PI * 1.15) * baseVolatility * 0.42;
        const driftB = Math.sin(ratio * Math.PI * 3.2) * baseVolatility * 0.12;
        const interpolatedValue = startValue + delta * eased;
        const value = step === steps ? endValue : interpolatedValue + driftA + driftB;
        const rawTime = Math.round(startTime + gapSeconds * ratio);
        const previousTime = points.length ? points[points.length - 1].time : startTime;
        const time = Math.min(endTime, Math.max(previousTime + 1, rawTime));

        points.push({
            time,
            value: Number(value.toFixed(6)),
        });
    }

    points[points.length - 1] = nextPoint;
    return points;
}

const LivePriceChart = ({ points, symbol, isPriceUp }) => {
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const seriesRef = useRef(null);
    const trendColor = isPriceUp ? "#22c55e" : "#e85b5b";
    const topFillColor = isPriceUp ? "rgba(34, 197, 94, 0.22)" : "rgba(232, 91, 91, 0.24)";
    const bottomFillColor = isPriceUp ? "rgba(34, 197, 94, 0.015)" : "rgba(232, 91, 91, 0.02)";

    useEffect(() => {
        if (!containerRef.current) return undefined;

        const chart = createChart(containerRef.current, {
            layout: {
                background: { color: "#081225" },
                textColor: "rgba(186, 197, 217, 0.78)",
                attributionLogo: false,
            },
            grid: {
                vertLines: { color: "rgba(110, 126, 166, 0.05)" },
                horzLines: { color: "rgba(110, 126, 166, 0.18)" },
            },
            crosshair: {
                vertLine: {
                    color: "rgba(144, 162, 197, 0.18)",
                    labelBackgroundColor: "#121c30",
                },
                horzLine: {
                    color: "rgba(144, 162, 197, 0.18)",
                    labelBackgroundColor: "#121c30",
                },
            },
            rightPriceScale: {
                borderColor: "rgba(126, 145, 179, 0.18)",
                scaleMargins: {
                    top: 0.08,
                    bottom: 0.08,
                },
            },
            timeScale: {
                borderColor: "rgba(126, 145, 179, 0.12)",
                timeVisible: true,
                secondsVisible: false,
                visible: false,
            },
            localization: {
                priceFormatter: formatCompactPrice,
            },
            width: containerRef.current.clientWidth,
            height: containerRef.current.clientHeight || 350,
        });

        const series = chart.addAreaSeries({
            lineColor: trendColor,
            topColor: topFillColor,
            bottomColor: bottomFillColor,
            lineWidth: 3,
            crosshairMarkerVisible: false,
            lastValueVisible: true,
            priceLineVisible: true,
            priceLineWidth: 1,
            priceLineStyle: 2,
            priceLineColor: trendColor,
        });

        chartRef.current = chart;
        seriesRef.current = series;

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            chart.applyOptions({
                width: entry.contentRect.width,
                height: entry.contentRect.height || 350,
            });
            chart.timeScale().fitContent();
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
            chart.remove();
            chartRef.current = null;
            seriesRef.current = null;
        };
    }, [symbol, trendColor, topFillColor, bottomFillColor]);

    useEffect(() => {
        if (!seriesRef.current) return;
        seriesRef.current.applyOptions({
            lineColor: trendColor,
            topColor: topFillColor,
            bottomColor: bottomFillColor,
            priceLineColor: trendColor,
        });
        seriesRef.current.setData(points);
        if (points.length > 1) {
            chartRef.current?.timeScale().fitContent();
        }
    }, [points, trendColor, topFillColor, bottomFillColor]);

    return <div ref={containerRef} className="w-full h-full" aria-label={`${symbol} live price chart`} />;
};


export default function TradePage() {
    const [modalView, setModalView] = useState('none');
    const [isCoinMenuOpen, setIsCoinMenuOpen] = useState(false);
    const [tradeDirection, setTradeDirection] = useState(null);
    const { symbol } = useParams(); // Get symbol from URL

// Find the coin from our COINS array based on the URL symbol, or default to BTC
const getInitialCoin = () => {
    if (symbol) {
        const foundCoin = COINS.find(c => c.symbol.toUpperCase() === symbol.toUpperCase());
        if (foundCoin) return foundCoin;
    }
    return COINS[0]; // Default to BTC
};

    const [selectedCoin, setSelectedCoin] = useState(getInitialCoin());

   // Effect to update the coin if the URL parameter changes while on the page
    useEffect(() => {
    setSelectedCoin(getInitialCoin());
    }, [symbol]);
    const [balance, setBalance] = useState(0);
    const [loadingChart, setLoadingChart] = useState(true);
    const [chartError, setChartError] = useState("");
    const [priceHistory, setPriceHistory] = useState([]);
    const [allCoinStats, setAllCoinStats] = useState([]);
    
    const [tradeState, setTradeState] = useState(loadTradeState());
    const [tradeDetail, setTradeDetail] = useState(null);
    const [waitingForResult, setWaitingForResult] = useState(false);
    
    const token = localStorage.getItem("token");
    const currentCoinStats = allCoinStats.find(c => c.symbol === selectedCoin.symbol) || null;
    const coinPrice = currentCoinStats ? currentCoinStats.quote.USD.price : null;
    const latestChartPrice = useMemo(() => {
        if (!priceHistory.length) return null;
        return priceHistory[priceHistory.length - 1].value;
    }, [priceHistory]);
    const chartOverlay = useMemo(() => {
        if (!priceHistory.length || !Number.isFinite(latestChartPrice)) {
            return { topPercent: 50 };
        }

        const values = priceHistory.map((point) => point.value);
        const rawMin = Math.min(...values);
        const rawMax = Math.max(...values);
        const padding = Math.max((rawMax - rawMin) * 0.18, latestChartPrice * 0.0025, 0.0001);
        const min = rawMin - padding;
        const max = rawMax + padding;
        const ratio = max === min ? 0.5 : (latestChartPrice - min) / (max - min);

        return {
            topPercent: Math.min(92, Math.max(8, (1 - ratio) * 100)),
        };
    }, [latestChartPrice, priceHistory]);
    const previousChartPrice = useMemo(() => {
        if (priceHistory.length < 2) return latestChartPrice;
        return priceHistory[priceHistory.length - 2].value;
    }, [latestChartPrice, priceHistory]);
    const isPriceUp = latestChartPrice >= previousChartPrice;

    useEffect(() => {
        setLoadingChart(true);
        setChartError("");
        setPriceHistory([]);

        let isMounted = true;

        const applyHistoryPoints = (points) => {
            const nextHistory = normalizeHistoryPoints(points);
            if (!isMounted || !nextHistory.length) return false;
            setPriceHistory(nextHistory);
            setLoadingChart(false);
            setChartError("");
            return true;
        };

        const appendPricePoint = (price) => {
            const numericPrice = Number(price);
            if (!isMounted || !Number.isFinite(numericPrice) || numericPrice <= 0) return false;

            setPriceHistory((prev) => {
                const nextPoint = {
                    time: Math.floor(Date.now() / 1000),
                    value: Number(numericPrice.toFixed(6)),
                };
                if (!prev.length) {
                    return [nextPoint];
                }

                const previousPoint = prev[prev.length - 1];
                if (previousPoint.time === nextPoint.time) {
                    return [...prev.slice(0, -1), nextPoint].slice(-180);
                }

                const interpolatedPoints = buildFlowingPoints(previousPoint, nextPoint);
                return [...prev, ...interpolatedPoints].slice(-180);
            });
            setLoadingChart(false);
            setChartError("");
            return true;
        };

        const fetchChartHistory = async () => {
            try {
                const res = await axios.get(`${MAIN_API_BASE}/prices/chart/${selectedCoin.symbol}`);
                return applyHistoryPoints(res.data?.points);
            } catch (error) {
                console.error(`Failed to fetch ${selectedCoin.symbol} chart history`, error);
                return false;
            }
        };

        const fetchAllStats = async () => {
            let listPrice;
            try {
                const res = await axios.get(`${MAIN_API_BASE}/prices`);
                if (!isMounted) return;

                const data = Array.isArray(res.data?.data) ? res.data.data : [];
                setAllCoinStats(data);

                const directPrice = res.data?.prices?.[selectedCoin.symbol];
                const coinStats = data.find((coin) => coin.symbol === selectedCoin.symbol);
                listPrice = directPrice ?? coinStats?.quote?.USD?.price;
            } catch (error) {
                console.error("Failed to fetch coin stats", error);
            }

            if (appendPricePoint(listPrice)) return;

            try {
                const res = await axios.get(`${MAIN_API_BASE}/prices/${selectedCoin.symbol}`);
                if (!appendPricePoint(res.data?.price) && isMounted) {
                    setChartError("Live market data is temporarily unavailable.");
                }
            } catch (error) {
                console.error(`Failed to fetch ${selectedCoin.symbol} price`, error);
                if (isMounted) {
                    setChartError("Live market data is temporarily unavailable.");
                    setLoadingChart(false);
                }
            }
        };

        const bootstrap = async () => {
            const hasHistory = await fetchChartHistory();
            await fetchAllStats();

            if (!hasHistory && isMounted && !priceHistory.length) {
                setLoadingChart(false);
            }
        };

        bootstrap();
        const interval = setInterval(fetchAllStats, 3000);
        const historyInterval = setInterval(fetchChartHistory, 30000);

        return () => {
            isMounted = false;
            clearInterval(interval);
            clearInterval(historyInterval);
        };
    }, [selectedCoin.symbol]);

    const fetchBalance = () => {
        if (!token) return;
        axios.get(`${MAIN_API_BASE}/balance`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => {
                const usdtAsset = (res.data.assets || []).find(a => a.symbol === 'USDT');
                if (usdtAsset) setBalance(parseFloat(usdtAsset.balance));
            })
            .catch(err => console.error("Failed to fetch balance:", err));
    };
    useEffect(fetchBalance, [token, tradeDetail]);

    const pollResult = async (trade_id, user_id) => {
        let tries = 0, trade = null;
        while (tries < 10 && (!trade || trade.result === "PENDING")) {
            try {
                const res = await axios.get(`${MAIN_API_BASE}/trade/history/${user_id}`, { headers: { Authorization: `Bearer ${token}` } });
                trade = res.data.find((t) => t.id === trade_id);
                if (trade && trade.result !== "PENDING") break;
            } catch {}
            await new Promise((r) => setTimeout(r, 1500));
            tries++;
        }
        setWaitingForResult(false);
        setTradeState(null);
        persistTradeState(null);
        if (trade && trade.result !== "PENDING") {
            setTradeDetail(trade);
            setModalView('result');
        } else {
            alert("Could not retrieve trade result. Please check your trade history.");
        }
    };

    const onTimerComplete = async () => {
        if (!tradeState) return;
        setWaitingForResult(true);
        await pollResult(tradeState.trade_id, tradeState.user_id);
    };
    
    const executeTrade = async ({ amount, duration, profitPercentage }) => {
        setModalView('none');
        if (!coinPrice || !token || tradeState) {
            alert(!token ? "Please log in to trade." : "A trade is already active.");
            return;
        }

        const user_id = parseJwt(token).id;
        try {
            const res = await axios.post(`${MAIN_API_BASE}/trade`, {
                user_id,
                direction: tradeDirection.toUpperCase(),
                amount: Number(amount),
                duration: Number(duration),
                symbol: selectedCoin.symbol,
                client_price: Number(coinPrice),
                profit_percentage: profitPercentage,
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            if (!res.data.trade_id) throw new Error("Backend did not return a trade_id");

            const newTradeState = createTradeState(res.data.trade_id, user_id, duration, amount, selectedCoin.symbol, tradeDirection.toUpperCase());
            setTradeState(newTradeState);
            persistTradeState(newTradeState);
        } catch (err) {
            alert(`Trade failed: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleOpenTradeModal = (direction) => {
        if (tradeState) return;
        setTradeDirection(direction);
        setModalView('trade');
    };
    
    useEffect(() => {
        if (tradeState) {
            setModalView('countdown');
        }
    }, [tradeState]);

    return (
        <div className="w-full max-w-full min-h-screen bg-[#0b1020] text-white overflow-x-hidden">
            
            {/* --- Header --- */}
            <header className="sticky top-0 z-20 bg-[#0b1020]/80 backdrop-blur-md p-4 flex items-center justify-between border-b border-gray-800">
                <h1 className="text-xl font-semibold text-white">Binary Trading</h1>
                <button 
                    onClick={() => setIsCoinMenuOpen(true)} 
                    disabled={!!tradeState}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1C2127] text-white font-bold text-base disabled:opacity-50 transition-colors hover:bg-gray-700"
                >
                    <Menu size={18} />
                    {selectedCoin.symbol}/USDT
                </button>
            </header>
            
            <main className="pb-40">
                <CoinStatsDisplay stats={currentCoinStats} />

                {/* --- Trading View Chart --- */}
                <div className="relative px-4">
                    <div className="relative overflow-hidden rounded-[28px] border border-slate-800/80 bg-[#060d1c] shadow-[0_24px_80px_rgba(2,8,23,0.55)]">
                        <div className={`pointer-events-none absolute inset-0 opacity-90 ${isPriceUp ? 'bg-[radial-gradient(circle_at_top_left,rgba(34,197,94,0.14),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(34,197,94,0.08),transparent_34%)]' : 'bg-[radial-gradient(circle_at_top_left,rgba(232,91,91,0.18),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(232,91,91,0.10),transparent_34%)]'}`} />
                        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),transparent_22%,transparent_78%,rgba(255,255,255,0.02))]" />
                        <div className="relative flex items-center justify-between px-5 pt-4">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Market</div>
                                <div className="mt-1 text-lg font-semibold text-white">{selectedCoin.symbol}/USDT</div>
                            </div>
                            <div className="rounded-full border border-slate-700/70 bg-slate-900/80 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-slate-300">
                                Live
                            </div>
                        </div>
                        <div className="relative h-[40vh] min-h-[350px]">
                            <LivePriceChart points={priceHistory} symbol={selectedCoin.symbol} isPriceUp={isPriceUp} />
                            {!loadingChart && !!priceHistory.length && (
                                <>
                                    <div
                                        className="pointer-events-none absolute left-4 right-[5.8rem] border-t border-dashed border-slate-400/30"
                                        style={{ top: `${chartOverlay.topPercent}%` }}
                                    />
                                    <div
                                        className={`pointer-events-none absolute right-0 -translate-y-1/2 rounded-l-xl px-3 py-2 text-sm font-bold text-white shadow-xl ${
                                            isPriceUp ? 'bg-green-500' : 'bg-red-500'
                                        }`}
                                        style={{ top: `${chartOverlay.topPercent}%` }}
                                    >
                                        {formatCompactPrice(latestChartPrice)}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    {loadingChart && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0b1020]">
                            <BeautifulLoader text="Refreshing Price..." />
                        </div>
                    )}
                    {!loadingChart && chartError && !priceHistory.length && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0b1020] px-6 text-center text-sm text-gray-300">
                            {chartError}
                        </div>
                    )}
                    {!loadingChart && !!priceHistory.length && (
                        <div className="absolute left-8 top-20 z-10 rounded-2xl border border-slate-800/80 bg-[#101726]/88 px-4 py-3 text-sm shadow-xl backdrop-blur">
                            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{selectedCoin.symbol}/USDT</div>
                            <div className={`mt-1 text-xl font-bold ${isPriceUp ? 'text-green-400' : 'text-red-400'}`}>
                                ${formatChartPrice(latestChartPrice)}
                            </div>
                        </div>
                    )}
                </div>

                 {/* --- Order Book --- */}
                <div className="mt-6 px-4">
                    <OrderBTC symbol={selectedCoin.symbol} />
                </div>
            </main>
            
            {/* --- Fixed Buy/Sell Buttons --- */}
            <div 
                className="fixed bottom-[65px] left-0 right-0 z-10 bg-gradient-to-t from-[#0b1020] via-[#0b1020] to-transparent pt-8 px-4 pb-4"
                style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
            >
                <div className="grid grid-cols-2 gap-4">
                    <button 
                        onClick={() => handleOpenTradeModal('buy')} 
                        disabled={!!tradeState} 
                        className="w-full py-4 rounded-xl font-bold text-lg bg-green-500 hover:bg-green-600 disabled:bg-gray-700 disabled:text-gray-400 transition-all duration-300 transform active:scale-95 flex items-center justify-center gap-2"
                    >
                        <TrendingUp size={22}/>
                        Buy Long
                    </button>
                    <button 
                        onClick={() => handleOpenTradeModal('sell')} 
                        disabled={!!tradeState} 
                        className="w-full py-4 rounded-xl font-bold text-lg bg-red-500 hover:bg-red-600 disabled:bg-gray-700 disabled:text-gray-400 transition-all duration-300 transform active:scale-95 flex items-center justify-center gap-2"
                    >
                        <TrendingDown size={22}/>
                        Sell Short
                    </button>
                </div>
            </div>

            {/* --- Modals --- */}
            <AnimatePresence>
                {modalView === 'trade' && (
                    <BinaryTradeModal direction={tradeDirection} balance={balance} onClose={() => setModalView('none')} onConfirm={executeTrade} />
                )}
                {modalView === 'countdown' && tradeState && (
                    <CountdownModal tradeDetails={tradeState} isWaiting={waitingForResult} onComplete={onTimerComplete} />
                )}
                {modalView === 'result' && tradeDetail && (
                    <BinaryResultModal tradeDetail={tradeDetail} onClose={() => { setTradeDetail(null); setModalView('none'); }} />
                )}
                {isCoinMenuOpen && (
                    <CoinSelectMenu 
                        isOpen={isCoinMenuOpen} 
                        onClose={() => setIsCoinMenuOpen(false)} 
                        onSelectCoin={(coin) => {
                            setSelectedCoin(coin);
                            setIsCoinMenuOpen(false);
                        }} 
                        currentCoinSymbol={selectedCoin.symbol} 
                        disabled={!!tradeState} 
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
